import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {EndpointService} from '../../../../../../@shared/endpoints.service';
import {UserSearchResult, UserEntityRelationWithUserDataResponse} from '../../../../../../@shared/models/response.module';
import {USM_USR_6} from '../../../../../../@shared/models/input.module';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-invite-user-modal',
  standalone: false,
  templateUrl: './invite-user-modal.html',
  styleUrl: './invite-user-modal.css'
})
export class InviteUserModal implements OnInit {
  @Input() companyId: number;
  @Input() existingUsers: UserEntityRelationWithUserDataResponse[] = [];

  // Modal state
  modalStep: 'search' | 'form' = 'search';

  // Search state
  searchQuery: string = '';
  searchResults: UserSearchResult[] = [];
  searchLoading: boolean = false;
  selectedUser: UserSearchResult | null = null;
  private searchSubject = new Subject<string>();

  // Invite form fields
  inviteEmail: string = '';
  inviteFirstName: string = '';
  inviteLastName: string = '';
  invitePhone: string = '';
  inviteRoleId: number = 2;
  inviteLoading: boolean = false;
  inviteError: string = '';
  submitted: boolean = false;

  // Role options
  roleOptions: { role_id: number, role_name: string, description: string }[] = [];

  // Validation helpers
  get isEmailInvalid(): boolean {
    if (!this.submitted) return false;
    if (!this.inviteEmail.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !emailRegex.test(this.inviteEmail);
  }

  get isFirstNameInvalid(): boolean {
    return this.submitted && !this.inviteFirstName.trim();
  }

  get isLastNameInvalid(): boolean {
    return this.submitted && !this.inviteLastName.trim();
  }

  get isFormValid(): boolean {
    if (!this.inviteEmail.trim() || !this.inviteFirstName.trim() || !this.inviteLastName.trim()) {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(this.inviteEmail);
  }

  constructor(
    public activeModal: NgbActiveModal,
    private endpointService: EndpointService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  ngOnInit() {
    this.initRoleOptions();
  }

  private initRoleOptions() {
    this.roleOptions = [
      { role_id: 1, role_name: this.translateService.instant('companyProfile.users.roleAdmin'), description: 'Full access to all features' },
      { role_id: 2, role_name: this.translateService.instant('companyProfile.users.roleMember'), description: 'Basic access to organisation' }
    ];
  }

  onSearchInput(query: string) {
    this.searchQuery = query;
    if (query.length >= 2) {
      this.searchSubject.next(query);
    } else {
      this.searchResults = [];
    }
  }

  performSearch(query: string) {
    if (query.length < 2) {
      this.searchResults = [];
      return;
    }

    this.searchLoading = true;
    this.endpointService.aqh_eve_67({ search: query }).subscribe({
      next: (res) => {
        const existingUserIds = this.existingUsers.map(u => u.user_id);
        this.searchResults = (res.data || []).filter(
          user => !existingUserIds.includes(user.user_id)
        );
        this.searchLoading = false;
      },
      error: () => {
        this.searchResults = [];
        this.searchLoading = false;
      }
    });
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
  }

  deselectUser() {
    this.selectedUser = null;
  }

  showCreateForm() {
    this.modalStep = 'form';
    if (this.searchQuery.includes('@')) {
      this.inviteEmail = this.searchQuery;
    }
  }

  backToSearch() {
    this.modalStep = 'search';
    this.inviteError = '';
    this.submitted = false;
  }

  addSelectedUser() {
    if (!this.selectedUser) return;

    this.inviteLoading = true;
    this.inviteError = '';

    const payload: USM_USR_6 = {
      email: this.selectedUser.email,
      first_name: this.selectedUser.first_name,
      last_name: this.selectedUser.last_name,
      phone: this.selectedUser.phone || '',
      role_id: this.inviteRoleId,
      entity_id: String(this.companyId),
      skip_invitation: 1,
      component_id: 'core'
    };

    this.endpointService.usm_usr_6(payload).subscribe({
      next: () => {
        this.inviteLoading = false;
        this.toastService.success(this.translateService.instant('companyProfile.addMember.success'));
        this.activeModal.close('added');
      },
      error: (err) => {
        this.inviteLoading = false;
        this.inviteError = err?.error?.message || this.translateService.instant('companyProfile.addMember.error');
        this.toastService.error(this.inviteError);
      }
    });
  }

  submitInvite() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('companyProfile.invite.validationError'));
      return;
    }

    this.inviteLoading = true;
    this.inviteError = '';

    const payload: USM_USR_6 = {
      email: this.inviteEmail.trim(),
      first_name: this.inviteFirstName.trim(),
      last_name: this.inviteLastName.trim(),
      phone: this.invitePhone.trim(),
      role_id: this.inviteRoleId,
      entity_id: String(this.companyId),
      skip_invitation: 0,
      component_id: 'core'
    };

    this.endpointService.usm_usr_6(payload).subscribe({
      next: () => {
        this.inviteLoading = false;
        this.toastService.success(this.translateService.instant('companyProfile.invite.success'));
        this.activeModal.close('invited');
      },
      error: (err) => {
        this.inviteLoading = false;
        this.inviteError = err?.error?.message || this.translateService.instant('companyProfile.invite.error');
        this.toastService.error(this.inviteError);
      }
    });
  }

  getInitials(firstName: string | null, lastName: string | null): string {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  }
}
